import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getQueryFn } from '@/lib/queryClient';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { primeLeagueCache } from '@/lib/leagueCache';
import { isWeb } from '@/lib/platform';
import { isNetworkError } from '@/lib/networkErrors';
import type { LeagueSummary } from '@/types/league';

export interface AuthenticatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  defaultLeagueId: string | null;
  pushPicksRemindersEnabled: boolean;
  pushScoringChangesEnabled: boolean;
  pushChatMessagesEnabled: boolean;
  pushLiveGameAlertsEnabled: boolean;
  pushMyPickUpdatesEnabled: boolean;
  pushFinalScoresEnabled: boolean;
  liveNotificationDelayMinutes: number;
  leagues: LeagueSummary[];
  isAdmin: boolean;
  isBetsAdmin: boolean;
}

interface AuthState {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** The session check failed without an answer from the API, so auth state is unknown. */
  hasConnectionError: boolean;
  retry: () => void;
}

/**
 * Hook to get the current authenticated user
 * Uses React Query to fetch user data from /api/auth/user
 */
export function useAuth(): AuthState {
  const queryClient = useQueryClient();
  const webAuthQueryOptions = isWeb
    ? {
        refetchOnMount: false as const,
        refetchOnWindowFocus: false as const,
        refetchOnReconnect: false as const,
      }
    : {};
  const { data: user, isLoading, isFetching, isError, refetch } = useQuery<AuthenticatedUser | null>({
    queryKey: ['/api/auth/user'],
    queryFn: getQueryFn<AuthenticatedUser | null>({ on401: 'returnNull' }),
    retry: (failureCount, error) =>
      isWeb ? failureCount < 5 : failureCount < 2 && isNetworkError(error),
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** Math.max(0, attemptIndex - 1), 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...webAuthQueryOptions,
  });

  const isAuthBootLoading = isLoading || (!isWeb && isFetching && !user);
  // Only a 401 means signed out (the query returns null). Any other failure leaves the stored
  // session untouched, so callers must not route to the landing/sign-in screens.
  const hasConnectionError = isError && !user && !isAuthBootLoading;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!user?.leagues) {
      queryClient.setQueryData(trpc.leagues.list.queryKey(), []);
      return;
    }

    // Auth returns a subset of the leagues.list payload; assert the compatible shape
    // to seed the tRPC-backed leagues list cache (matters on native where staleTime is Infinity).
    queryClient.setQueryData(
      trpc.leagues.list.queryKey(),
      user.leagues as unknown as RouterOutputs['leagues']['list'],
    );
    user.leagues.forEach((league) => {
      if (!league?.id || String(league.id) === 'undefined') {
        return;
      }
      const leagueId = String(league.id);
      const seasonScopedKey: [string, string, string] = [
        '/api/leagues',
        leagueId,
        league.resolvedSeasonId ?? 'active',
      ];
      const existing = queryClient.getQueryData<LeagueSummary>(seasonScopedKey);
      primeLeagueCache(queryClient, existing ?? league);
    });
  }, [queryClient, user]);

  return useMemo(
    () => ({
      user: user || null,
      isLoading: isAuthBootLoading,
      isAuthenticated: !!user,
      hasConnectionError,
      retry,
    }),
    [user, isAuthBootLoading, hasConnectionError, retry]
  );
}
